import type { Dispatch } from "react";
import { useEffect } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";

import type { ICampaignLocationAndLanguage } from "app/components/campaigns/campaigns-wizard/CampaignsWizardConsts";
import {
    getLanguagesAndLocationsForCampaigns,
    getLanguagesForCampaigns,
    getLocationsForCampaigns,
} from "app/components/campaigns/shared/locationsAndLanguagesUtils";
import useLocalStorage from "app/custom-hooks/useLocalStorage";
import { RANK_TRACKER_LOCALE_CONFIGS_KEY } from "app/common/campaigns/KeywordsCampaignConsts";
import { getIsRTCreateCampaignsAvailable } from "app/pages/digital-marketing/keywords-campaigns/infra/KeywordsCampaignsInfraUtils";
import type { RTContextActions } from "app/pages/digital-marketing/keywords-campaigns/infra/RTContext.consts";
import { ERTContextReducerTypes } from "app/pages/digital-marketing/keywords-campaigns/infra/RTContext.consts";

export const useLanguagesAndLocations = (
    dispatch: Dispatch<RTContextActions>,
    locationsAndLanguagesLists: ICampaignLocationAndLanguage,
) => {
    const { state } = useCurrentStateAndParams();

    const [, setLocationsAndLanguagesFromLocalStorage] = useLocalStorage(RANK_TRACKER_LOCALE_CONFIGS_KEY);

    useEffect(() => {
        const updateLanguagesAndLocations = async () => {
            let locationsAndLanguages: ICampaignLocationAndLanguage = {
                languagesList: [],
                locationsList: [],
            };
            const useDemoCampaignsApi = !getIsRTCreateCampaignsAvailable();
            const shouldGetLocations = state.name.includes("settings") || state.name.toLowerCase().includes("wizard");

            try {
                if (
                    !locationsAndLanguagesLists.languagesList.length &&
                    !locationsAndLanguagesLists.locationsList.length &&
                    shouldGetLocations
                ) {
                    locationsAndLanguages = await getLanguagesAndLocationsForCampaigns(useDemoCampaignsApi);
                    setLocationsAndLanguagesFromLocalStorage(JSON.stringify(locationsAndLanguages.languagesList));
                } else if (!locationsAndLanguagesLists.languagesList.length && !shouldGetLocations) {
                    locationsAndLanguages.languagesList = await getLanguagesForCampaigns(useDemoCampaignsApi);
                } else if (locationsAndLanguagesLists.languagesList.length && shouldGetLocations) {
                    locationsAndLanguages.locationsList = await getLocationsForCampaigns(useDemoCampaignsApi);
                } else {
                    dispatch({
                        type: ERTContextReducerTypes.UpdateLoadingLanguagesAndLocationsState,
                        payload: { isLoading: false },
                    });
                    return;
                }

                dispatch({
                    type: ERTContextReducerTypes.UpdateLoadingLanguagesAndLocationsState,
                    payload: { isLoading: false },
                });
                dispatch({
                    type: ERTContextReducerTypes.UpdateLocationsAndLanguagesLists,
                    payload: { locationsAndLanguagesLists: locationsAndLanguages },
                });
            } catch (error) {
                console.error("Error updating languages and locations:", error);
                dispatch({
                    type: ERTContextReducerTypes.UpdateLoadingLanguagesAndLocationsState,
                    payload: { isLoading: false },
                });
            }
        };
        void updateLanguagesAndLocations();
    }, [
        dispatch,
        locationsAndLanguagesLists.languagesList.length,
        locationsAndLanguagesLists.locationsList.length,
        setLocationsAndLanguagesFromLocalStorage,
        state.name,
    ]);
};
